import {
  SelectItem,
  useSelectedGuidsAsSelectItemsQuery,
} from '@kistill/client/data-access'
import React, { createContext, useContext, useEffect, useState } from 'react'

interface SelectedGuidsContextType {
  selectedGuids: string[]
  setSelectedGuids: (guids: string[]) => void
  source: 'origin' | 'itemCollection' | null
  setSource: (source: 'origin' | 'itemCollection' | null) => void
  selectedGuidsSelectItems: SelectItem[]
}

const SelectedGuidsContext = createContext<
  SelectedGuidsContextType | undefined
>(undefined)

export const SelectedGuidsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedGuids, setSelectedGuids] = useState<string[]>([])
  const [source, setSource] = useState<'origin' | 'itemCollection' | null>(null)
  const [selectedGuidsSelectItems, setSelectedGuidsSelectItems] = useState<
    SelectItem[]
  >([])

  const { refetch } = useSelectedGuidsAsSelectItemsQuery({
    variables: { selectedGuids, source: source || '' },
    skip: !source || selectedGuids.length === 0,
  })

  useEffect(() => {
    if (source && selectedGuids.length > 0) {
      refetch({ selectedGuids, source }).then((result) => {
        if (result.data?.selectedGuidsAsSelectItems) {
          setSelectedGuidsSelectItems(result.data.selectedGuidsAsSelectItems)
        }
      })
    } else {
      setSelectedGuidsSelectItems([])
    }
  }, [selectedGuids, source, refetch])

  return (
    <SelectedGuidsContext.Provider
      value={{
        selectedGuids,
        setSelectedGuids,
        source,
        setSource,
        selectedGuidsSelectItems,
      }}
    >
      {children}
    </SelectedGuidsContext.Provider>
  )
}

export const useSelectedGuids = (): SelectedGuidsContextType => {
  const context = useContext(SelectedGuidsContext)
  if (!context) {
    throw new Error(
      'useSelectedGuids must be used within a SelectedGuidsProvider.',
    )
  }
  return context
}
